import React, { useEffect } from 'react';
import { SelectField } from '@flos/react-ui';
import useAppState from '../../state/appState';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { CarBrands as CarBrandsType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue } from '../values/Get';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const getCarBrandsArray = () => {
    return [
        {
            value: 'select',
            caption: 'Vælg...'
        },
        {
            value: 'Alfa Romeo',
            caption: 'Alfa Romeo'
        },
        {
            value: 'audi',
            caption: 'Audi'
        },
        {
            value: 'bmw',
            caption: 'BMW'
        },
        {
            value: 'Chevrolet/Daewoo',
            caption: 'Chevrolet/Daewoo'
        },
        {
            value: 'chrysler',
            caption: 'Chrysler'
        },
        {
            value: 'citroen',
            caption: 'Citroen'
        },
        {
            value: 'dacia',
            caption: 'Dacia'
        },
        {
            value: 'ds',
            caption: 'DS'
        },
        {
            value: 'fiat',
            caption: 'Fiat'
        },
        {
            value: 'ford',
            caption: 'Ford'
        },
        {
            value: 'honda',
            caption: 'Honda'
        },
        {
            value: 'hyundai',
            caption: 'Hyundai'
        },
        {
            value: 'iveco',
            caption: 'Iveco'
        },
        {
            value: 'jaguar',
            caption: 'Jaguar'
        },
        {
            value: 'jeep',
            caption: 'Jeep'
        },
        {
            value: 'kia',
            caption: 'Kia'
        },
        {
            value: 'lancia',
            caption: 'Lancia'
        },
        {
            value: 'Land Rover',
            caption: 'Land Rover'
        },
        {
            value: 'lexus',
            caption: 'Lexus'
        },
        {
            value: 'maxus',
            caption: 'Maxus'
        },
        {
            value: 'mazda',
            caption: 'Mazda'
        },
        {
            value: 'mercedes',
            caption: 'Mercedes'
        },
        {
            value: 'mg',
            caption: 'MG'
        },
        {
            value: 'mini',
            caption: 'Mini'
        },
        {
            value: 'mitsubishi',
            caption: 'Mitsubishi'
        },
        {
            value: 'nissan',
            caption: 'Nissan'
        },
        {
            value: 'opel',
            caption: 'Opel'
        },
        {
            value: 'peugeot',
            caption: 'Peugeot'
        },
        {
            value: 'porsche',
            caption: 'Porsche'
        },
        {
            value: 'renault',
            caption: 'Renault'
        },
        {
            value: 'rover',
            caption: 'Rover'
        },
        {
            value: 'saab',
            caption: 'Saab'
        },
        {
            value: 'seat',
            caption: 'SEAT'
        },
        {
            value: 'skoda',
            caption: 'Skoda'
        },
        {
            value: 'smart',
            caption: 'Smart'
        },
        {
            value: 'SsangYong',
            caption: 'SsangYong'
        },
        {
            value: 'subaru',
            caption: 'Subaru'
        },
        {
            value: 'suzuki',
            caption: 'Suzuki'
        },
        {
            value: 'tesla',
            caption: 'Tesla'
        },
        {
            value: 'toyota',
            caption: 'Toyota'
        },
        {
            value: 'volkswagen',
            caption: 'Volkswagen'
        },
        {
            value: 'volvo',
            caption: 'Volvo'
        },
        {
            value: 'other',
            caption: 'Andet'
        }
    ];
};

const CarBrands = ({ elementId, caption, placeholder, helpText }: CarBrandsType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = CarBrands.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');

    type Options = {
        value: string;
        caption: string;
    };

    const [carBrands, setCarBrands] = React.useState<Options[]>([]);

    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        const carBrands = getCarBrandsArray();
        setCarBrands(carBrands);
    }, []);

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = CarBrands.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <SelectField
                required={isRequiredField}
                label={placeholder}
                id={elementId}
                key={elementId}
                options={carBrands.map((option) => ({
                    label: option.caption,
                    value: option.value
                }))}
                isValid={isValid}
                helpText={helpText}
                errorText={errorMessage}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    const value = event.target.value;
                    setFormValue(elementId, value);
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                value={value}
            />
        </>
    );
};

CarBrands.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as CarBrandsType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    const carBrands = getCarBrandsArray();
    const option = carBrands.find((item) => item.value === value);
    const caption = option?.caption;

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={caption} />;
};

CarBrands.getValueSubmit = (formValues: FormValues, elementId: string, label: string): ClaimFormSubmitFieldValue => {
    const value = CarBrands.getValue(formValues, elementId);
    const valueStr = CarBrands.getValueHuman(formValues, elementId);

    const valueId = { string: value };

    return getClaimFormSubmitValue('carbrands', elementId, label, valueStr, valueId);
};

CarBrands.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

CarBrands.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = CarBrands.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

CarBrands.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = CarBrands.getValue(formValues, elementId);
    return value;
};

CarBrands.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return getIsValid(formConfig, formValues, elementId);
};

CarBrands.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }

    const isValid = CarBrands.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default CarBrands;
